<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import DOMPurify from 'dompurify';
import MarkdownIt from 'markdown-it';

const props = defineProps({
  templateData: { type: Object, default: () => ({}) },
  previewHtml: { type: String, default: '' },
});

const { t } = useI18n();
const device = ref('desktop');
const markdown = new MarkdownIt({ html: false, linkify: true, breaks: true });

const exampleVariables = computed(() => ({
  '{{contact.name}}': t(
    'CAMPAIGN.EMAIL.TEMPLATES.PREVIEW.EXAMPLES.CONTACT_NAME'
  ),
  '{{contact.first_name}}': t(
    'CAMPAIGN.EMAIL.TEMPLATES.PREVIEW.EXAMPLES.CONTACT_FIRST_NAME'
  ),
  '{{contact.email}}': t(
    'CAMPAIGN.EMAIL.TEMPLATES.PREVIEW.EXAMPLES.CONTACT_EMAIL'
  ),
  '{{agent.name}}': t('CAMPAIGN.EMAIL.TEMPLATES.PREVIEW.EXAMPLES.AGENT_NAME'),
  '{{inbox.name}}': t('CAMPAIGN.EMAIL.TEMPLATES.PREVIEW.EXAMPLES.INBOX_NAME'),
  '{{account.name}}': t(
    'CAMPAIGN.EMAIL.TEMPLATES.PREVIEW.EXAMPLES.ACCOUNT_NAME'
  ),
}));

const escapeHtml = value =>
  String(value || '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

const renderLiquidExamples = value => {
  let renderedValue = String(value || '');
  Object.entries(exampleVariables.value).forEach(([token, example]) => {
    const variableName = token.slice(2, -2).replaceAll('.', '\\.');
    renderedValue = renderedValue.replace(
      new RegExp(`{{\\s*${variableName}\\s*}}`, 'g'),
      example
    );
  });
  return renderedValue.replace(
    /{{\s*[^}]+\s*}}/g,
    t('CAMPAIGN.EMAIL.TEMPLATES.PREVIEW.EXAMPLE_VALUE')
  );
};

const renderPlainText = value =>
  renderLiquidExamples(escapeHtml(value)).replaceAll('\n', '<br>');

const renderMarkdown = value =>
  DOMPurify.sanitize(markdown.render(renderLiquidExamples(value || '')));

const localPreviewHtml = computed(() => {
  const data = props.templateData;
  if (data.template_type === 'message') {
    return `<!doctype html>
      <html lang="pt-BR">
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1">
          <style>
            * { box-sizing: border-box; }
            body { margin: 0; padding: 24px; background: #f4f6f8; color: #1f2937; font: 15px/1.6 Arial, sans-serif; }
            .conversation { max-width: 680px; min-height: 420px; margin: 0 auto; padding: 24px; background: #fff; border: 1px solid #e5e7eb; }
            .bubble { max-width: 82%; margin-left: auto; padding: 12px 14px; border-radius: 8px 8px 2px 8px; background: #1f6feb; color: #fff; overflow-wrap: anywhere; }
          </style>
        </head>
        <body>
          <main class="conversation">
            <div class="bubble">${renderMarkdown(data.message || t('CAMPAIGN.EMAIL.TEMPLATES.FORM.MESSAGE_PLACEHOLDER'))}</div>
          </main>
        </body>
      </html>`;
  }
  const body = data.html_content
    ? renderLiquidExamples(data.html_content)
    : renderMarkdown(data.message);
  const logo = data.logo_url
    ? `<img src="${escapeHtml(data.logo_url)}" alt="" style="display:block;max-width:160px;max-height:64px;margin:0 0 24px;object-fit:contain">`
    : '';
  const button = data.button_text
    ? `<p style="margin:28px 0"><a href="${escapeHtml(data.button_url || '#')}" style="display:inline-block;background:#1f6feb;color:#fff;text-decoration:none;padding:11px 18px;border-radius:6px;font-weight:600">${renderPlainText(data.button_text)}</a></p>`
    : '';
  const signature = data.signature
    ? `<div style="margin-top:28px;color:#4b5563">${renderMarkdown(data.signature)}</div>`
    : '';

  return `<!doctype html>
    <html lang="pt-BR">
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1">
        <style>
          * { box-sizing: border-box; }
          body { margin: 0; padding: 24px; background: #f4f6f8; color: #1f2937; font: 15px/1.6 Arial, sans-serif; }
          .mail { max-width: 680px; margin: 0 auto; background: #fff; border: 1px solid #e5e7eb; }
          .header { padding: 18px 24px; border-bottom: 1px solid #e5e7eb; color: #6b7280; font-size: 13px; }
          .content { padding: 28px 24px; overflow-wrap: anywhere; }
          h1, h2, h3, p { margin-top: 0; }
          img { max-width: 100%; height: auto; }
          .footer { padding: 18px 24px; border-top: 1px solid #e5e7eb; color: #6b7280; font-size: 11px; text-align: center; }
        </style>
      </head>
      <body>
        <article class="mail">
          <div class="header">${renderPlainText(data.sender_name || t('CAMPAIGN.EMAIL.TEMPLATES.PREVIEW.DEFAULT_SENDER'))}<br><strong>${renderPlainText(data.subject || t('CAMPAIGN.EMAIL.TEMPLATES.PREVIEW.DEFAULT_SUBJECT'))}</strong></div>
          <div class="content">${logo}${body}${button}${signature}</div>
          <div class="footer">${t('CAMPAIGN.EMAIL.TEMPLATES.PREVIEW.FOOTER_TEXT')}<br><a href="#">${t('CAMPAIGN.EMAIL.TEMPLATES.PREVIEW.UNSUBSCRIBE')}</a></div>
        </article>
      </body>
    </html>`;
});

const iframeDocument = computed(() =>
  props.previewHtml
    ? renderLiquidExamples(props.previewHtml)
    : localPreviewHtml.value
);

const previewNotice = computed(() =>
  props.templateData.template_type === 'message'
    ? t('CAMPAIGN.EMAIL.TEMPLATES.PREVIEW.MESSAGE_NOTICE')
    : t('CAMPAIGN.EMAIL.TEMPLATES.PREVIEW.FOOTER_NOTICE')
);
</script>

<template>
  <section class="flex min-h-0 flex-col bg-n-alpha-2 lg:h-full">
    <header
      class="flex flex-wrap items-center justify-between gap-3 border-b border-n-weak px-4 py-3"
    >
      <div>
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.EMAIL.TEMPLATES.PREVIEW.TITLE') }}
        </h3>
        <p class="text-xs text-n-slate-10">
          {{ t('CAMPAIGN.EMAIL.TEMPLATES.PREVIEW.SANDBOX') }}
        </p>
      </div>
      <div class="flex rounded-md border border-n-weak bg-n-solid-2 p-0.5">
        <button
          type="button"
          class="inline-flex size-8 items-center justify-center rounded text-n-slate-11"
          :class="{ 'bg-n-alpha-3 text-n-slate-12': device === 'desktop' }"
          :aria-label="t('CAMPAIGN.EMAIL.TEMPLATES.PREVIEW.DESKTOP')"
          :title="t('CAMPAIGN.EMAIL.TEMPLATES.PREVIEW.DESKTOP')"
          @click="device = 'desktop'"
        >
          <span class="i-lucide-monitor size-4" />
        </button>
        <button
          type="button"
          class="inline-flex size-8 items-center justify-center rounded text-n-slate-11"
          :class="{ 'bg-n-alpha-3 text-n-slate-12': device === 'mobile' }"
          :aria-label="t('CAMPAIGN.EMAIL.TEMPLATES.PREVIEW.MOBILE')"
          :title="t('CAMPAIGN.EMAIL.TEMPLATES.PREVIEW.MOBILE')"
          @click="device = 'mobile'"
        >
          <span class="i-lucide-smartphone size-4" />
        </button>
      </div>
    </header>

    <div class="flex flex-1 justify-center overflow-auto p-3 sm:p-5">
      <iframe
        data-testid="email-template-preview"
        sandbox=""
        referrerpolicy="no-referrer"
        :srcdoc="iframeDocument"
        :title="t('CAMPAIGN.EMAIL.TEMPLATES.PREVIEW.IFRAME_TITLE')"
        class="min-h-[34rem] border border-n-weak bg-white shadow-sm transition-[width]"
        :class="device === 'mobile' ? 'w-[23.4375rem] max-w-full' : 'w-full'"
      />
    </div>

    <footer class="border-t border-n-weak px-4 py-3">
      <p class="flex items-start gap-2 text-xs leading-5 text-n-slate-10">
        <span
          class="i-lucide-shield-check mt-0.5 size-4 shrink-0 text-n-teal-10"
        />
        {{ previewNotice }}
      </p>
      <div
        class="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-n-slate-10"
      >
        <span v-for="(value, token) in exampleVariables" :key="token">
          <code class="text-n-blue-11">{{ token }}</code>
          {{ t('CAMPAIGN.EMAIL.TEMPLATES.PREVIEW.EXAMPLE_SEPARATOR') }}
          {{ value }}
        </span>
      </div>
    </footer>
  </section>
</template>
